import { memo, useCallback } from 'react';

import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import {
  getWorkspacePanelLabel,
  type WorkspacePanelContext,
} from '~/components/v2/components/shared/panel/getWorkspacePanelLabel';
import { CollapsePanelLeftIcon } from '~/components/v2/components/shared/panel/panelCollapseIcons';
import { useShowWorkspaceSplitTopbar } from '~/components/v2/components/shared/panel/useShowWorkspaceSplitTopbar';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';

export type CollapseWorkspacePanelButtonProps = {
  panelContext: WorkspacePanelContext;
};

/** Collapses the middle workspace column while a canvas is open for editing. */
export const CollapseWorkspacePanelButton = memo(({ panelContext }: CollapseWorkspacePanelButtonProps) => {
  const show = useShowWorkspaceSplitTopbar();
  const setBoardViewCollapsed = useWorkspaceRightPanelStore((state) => state.setBoardViewCollapsed);
  const label = getWorkspacePanelLabel(panelContext, 'collapse');

  const handleCollapse = useCallback(() => {
    setBoardViewCollapsed(true);
  }, [setBoardViewCollapsed]);

  if (!show) {
    return null;
  }

  return (
    <Tooltip label={label}>
      <IconButton
        appearance="ghost"
        color="gray"
        data-testid="COLLAPSE_WORKSPACE_PANEL_BUTTON"
        icon={CollapsePanelLeftIcon}
        label={label}
        onClick={handleCollapse}
        size="medium"
      />
    </Tooltip>
  );
});

CollapseWorkspacePanelButton.displayName = 'CollapseWorkspacePanelButton';
